import React, {
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import {useForm, Controller} from 'react-hook-form';
import {Box, Typography} from '@mui/material';
import ArrCheckbox from '../../../../common/arr-checkbox/ArrCheckbox';
import ArrSingleSelectDropdown from '../../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrGroupedSelectDropdown from '../../../../common/arr-select-dropdown/ArrGroupedSelectDropdown';
import ArrTextField from '../../../../common/arr-text-field/ArrTextField';
import ArrButton from '../../../../common/arr-button/ArrButton';
import ArrTooltipWrapper from '../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import ArrRadio from '../../../../common/arr-radio/ArrRadio';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import {ISelectItem} from '../../../../../shared/types';
import ArrDataGridPro from '../../../../common/arr-data-grid-pro/ArrDataGridPro'; //
import {GridRenderCellParams} from '@mui/x-data-grid';
import './DatComponent.scss';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../../../shared/interfaces';
import TagsTreePanel from '../../common/tags-tree-panel/TagsTreePanel';
import ArrModal from '../../../../common/arr-modal/ArrModal';
import ArrFilterChip from '../../../../common/arr-filter-chip/ArrFilterChip';
import {t} from 'i18next';
import {
  useGetDatComponent,
  useGetTemplateDatComponent,
} from '../../../../../hooks/productions/useProductions';
import {
  GetDatComponentResponse,
  GetTemplateDatComponentResponse,
} from '../../../../../api/types/production.type';
import {ModuleType, FormMode} from '../../CreateNewProductionUtils';
import {useSelector, useDispatch} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import MenuIcon from '@mui/icons-material/Menu';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {setIsFieldsChanged} from '../../../../../redux/slice/productionSlice';

const RowReorderIcon: React.FC<any> = (props) => (
  <MenuIcon {...props} fontSize="small" />
);

interface DatFieldHeaderNameCellProps {
  rowId: number;
  value: string;
  isViewMode: boolean;
  onChange: (rowId: number, value: string) => void;
}

const DatFieldHeaderNameCell: React.FC<DatFieldHeaderNameCellProps> = ({
  rowId,
  value,
  isViewMode,
  onChange,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const intendedSelectionRef = useRef<{start: number; end: number} | null>(
    null
  );

  useLayoutEffect(() => {
    if (intendedSelectionRef.current && inputRef.current) {
      const {start, end} = intendedSelectionRef.current;
      if (
        inputRef.current.selectionStart !== start ||
        inputRef.current.selectionEnd !== end
      ) {
        inputRef.current.setSelectionRange(start, end);
      }
    }
  });

  return (
    <ArrTextField
      inputRef={inputRef}
      value={value}
      onChange={(e) => {
        if (isViewMode) return;
        intendedSelectionRef.current = {
          start: e.target.selectionStart ?? e.target.value.length,
          end: e.target.selectionEnd ?? e.target.value.length,
        };
        onChange(rowId, e.target.value);
      }}
      onSelect={(e) => {
        if (isViewMode) return;
        const target = e.target as HTMLInputElement;
        intendedSelectionRef.current = {
          start: target.selectionStart ?? target.value.length,
          end: target.selectionEnd ?? target.value.length,
        };
      }}
      onKeyDown={(e) => {
        if (
          e.key === 'ArrowLeft' ||
          e.key === 'ArrowRight' ||
          e.key === 'ArrowUp' ||
          e.key === 'ArrowDown'
        ) {
          e.stopPropagation();
        }
      }}
      sizeVariant="small"
      dataTestId={`DATFL_${rowId}`}
      state={isViewMode ? 'inactive' : 'enabled'}
      slotProps={{
        input: {
          'aria-disabled': isViewMode,
          readOnly: isViewMode,
          tabIndex: isViewMode ? -1 : undefined,
        },
      }}
    />
  );
};

interface DATField {
  id: number;
  category: string;
  sourceField: string;
  datFieldHeaderName: string;
  scrubRedactions: boolean;
  scrubPriv: boolean;
  selectedTags?: {id: string; label: string}[];
}

interface DatComponentProps {
  productionId: number;
  isProductionSet: boolean;
  exportProductionId?: number;
  tagTreeData?: TreeViewBaseItem<ExtendedTreeItemProps>[];
  onFieldsChange?: (fields: DATField[]) => void;
  mode?: FormMode;
  isTemplate?: boolean;
  templateId?: number;
}

const DatComponent: React.FC<DatComponentProps> = ({
  productionId,
  isProductionSet,
  exportProductionId = 0,
  tagTreeData = [],
  onFieldsChange,
  mode = FormMode.CREATE,
  isTemplate = false,
  templateId,
}) => {
  const dispatch = useDispatch();

  const isViewMode = mode === FormMode.VIEW;
  const datGridTableRef = useRef<HTMLDivElement | null>(null);
  const datComponentRef = useRef<HTMLDivElement | null>(null);
  const datViewModeFocusRingTargetsRef = useRef<
    Array<{
      element: HTMLElement;
      focusHandler: EventListener;
      blurHandler: EventListener;
    }>
  >([]);
  const datSkippedDropdownTargetsRef = useRef<
    Array<{
      element: HTMLElement;
      focusHandler: EventListener;
    }>
  >([]);
  const datLastTabWasShiftRef = useRef<boolean>(false);
  const viewModeDisabledA11yProps = isViewMode
    ? ({
        'aria-disabled': true,
        tabIndex: -1,
      } as const)
    : {};
  const getDatTooltipIconA11yProps = (label: string) => ({
    role: 'button' as const,
    tabIndex: 0,
    'aria-label': label,
  });
  // Function to get current DAT configuration
  const getCurrentConfig = () => {
    return {
      ansiFormatType: encodingDropdownValue?.id,
      fieldSeparator: fieldSeparator?.id
        ? parseInt(fieldSeparator.id.toString())
        : undefined,
      textQualifier: textQualifier?.id
        ? parseInt(textQualifier.id.toString())
        : undefined,
      newLineSeparator: newLine?.id
        ? parseInt(newLine.id.toString())
        : undefined,
      multiValue: multiValue?.id
        ? parseInt(multiValue.id.toString())
        : undefined,
      dateFormatID: dateFormat?.id
        ? parseInt(dateFormat.id.toString())
        : undefined,
      dateFormat: dateFormat?.label,
      overwriteMasterDate: false, // TODO: Add state variable for this
      isAnsi: encoding === 'rdbANSI', // true if ANSI is selected
      eTextFormat: encoding === 'lstANSIType' ? 1 : 0, // 1 if Unicode, 0 if ANSI
      prependCarriageReturn: false, // TODO: Add state variable for this
    };
  };

  // Expose getCurrentConfig function globally for parent to access
  (window as any).getDatConfig = getCurrentConfig;
  // Get Redux state
  const selectedSecurityGroupId = useSelector(
    (state: RootState) => state.production.securityGroupId
  );
  const selectedProductionSetId = useSelector(
    (state: RootState) => state.production.productionSetId
  );

  // Determine moduleType from isProductionSet using enum
  const moduleType = useMemo(
    () => (isProductionSet ? ModuleType.PRODUCTION : ModuleType.EXPORT),
    [isProductionSet]
  );

  // API call to get DAT component data
  const {
    data: datComponentDataFromProduction,
    isLoading: isDatComponentLoadingFromProduction,
    error: datComponentErrorFromProduction,
  } = useGetDatComponent(
    {
      SecurityGroupId: selectedSecurityGroupId,
      moduletype: moduleType,
      parentsetid: selectedProductionSetId,
      productionId: productionId,
      exportProductionId: exportProductionId || 0,
    },
    !!selectedSecurityGroupId &&
      !!selectedProductionSetId &&
      !isTemplate &&
      productionId > 0
  );

  const {
    data: datComponentDataFromTemplate,
    isLoading: isDatComponentLoadingFromTemplate,
    error: datComponentErrorFromTemplate,
  } = useGetTemplateDatComponent(
    {
      templateId: templateId!,
      securityGroupId: selectedSecurityGroupId,
    },
    !!selectedSecurityGroupId && isTemplate && !!templateId
  );

  // Map template response to production response structure
  const mapTemplateToProduction = (
    templateData: GetTemplateDatComponentResponse
  ): GetDatComponentResponse => {
    return {
      ansiFormatList: templateData.listOfANSIType,
      datSelectedANSI: templateData.ansiFormatType,
      columnDelimiterList: templateData.columnDelimiterList,
      dataDelimiterList: templateData.dataDelimiterList,
      textQualifierList: templateData.textQualifierList,
      multiline: templateData.multiline,
      dateFormats: templateData.dateFormats,
      mappingRowCount: templateData.datFieldMappingDataList?.length || 0,
      modelData: {
        listOfANSIType: templateData.listOfANSIType,
        ansiFormatType: templateData.ansiFormatType,
        fieldSeparator: templateData.fieldSeparator,
        textQualifier: templateData.textQualifier,
        newLineSeparator: templateData.newLineSeparator,
        multiValue: templateData.multiValue,
        dateFormatID: templateData.dateFormatID,
        datFieldMappingDataList: templateData.datFieldMappingDataList,
        datTypeList: templateData.datTypeList,
        overwriteMasterDate: templateData.overwriteMasterDate,
        eTextFormat: templateData.textFormat,
        prependCarriageReturn: templateData.prependCarriageReturn,
        datCategoryList: templateData.datCategoryList,
      },
    };
  };

  // Use the appropriate data based on isTemplate flag - memoize to prevent infinite loops
  const datComponentData: GetDatComponentResponse | undefined = useMemo(() => {
    if (isTemplate && datComponentDataFromTemplate) {
      return mapTemplateToProduction(datComponentDataFromTemplate);
    }
    return datComponentDataFromProduction;
  }, [
    isTemplate,
    datComponentDataFromTemplate,
    datComponentDataFromProduction,
  ]);

  const isDatComponentLoading = isTemplate
    ? isDatComponentLoadingFromTemplate
    : isDatComponentLoadingFromProduction;
  const datComponentError = isTemplate
    ? datComponentErrorFromTemplate
    : datComponentErrorFromProduction;

  // State for dropdown options (populated from API)
  const [ansiFormatOptions, setAnsiFormatOptions] = useState<ISelectItem[]>([]);
  const [dateFormatOptions, setDateFormatOptions] = useState<ISelectItem[]>([]);
  const [fieldSeparatorOptions, setFieldSeparatorOptions] = useState<
    ISelectItem[]
  >([]);
  const [multiValueOptions, setMultiValueOptions] = useState<ISelectItem[]>([]);
  const [textQualifierOptions, setTextQualifierOptions] = useState<
    ISelectItem[]
  >([]);
  const [newLineOptions, setNewLineOptions] = useState<ISelectItem[]>([]);
  // Static category options for table dropdown
  const categoryOptions: ISelectItem[] = [
    {id: 'Field', name: 'Field'},
    {id: 'Tags', name: 'Tags'},
  ];
  const [groupedFieldOptions, setGroupedFieldOptions] = useState<any[]>([]);
  const [componentIsDirty, setComponentIsDirty] = useState<boolean>(false);

  // Populate dropdown options from API response
  useEffect(() => {
    if (datComponentData) {
      // ANSI Format options
      const ansiOptions =
        datComponentData.ansiFormatList?.map((item) => ({
          id: item.ansiTypeID,
          name: item.ansiTypeLabel,
        })) || [];
      setAnsiFormatOptions(ansiOptions);

      // Set initial encoding (ANSI/Unicode) based on API response eTextFormat
      // eTextFormat: 1 = Unicode, 0 = ANSI
      if (datComponentData.modelData?.eTextFormat === 1) {
        setEncoding('lstANSIType');
      } else {
        setEncoding('rdbANSI');
      }

      // Set default ANSI selection based on datSelectedANSI
      if (datComponentData.datSelectedANSI && ansiOptions.length > 0) {
        const defaultAnsi = ansiOptions.find(
          (option) => option.id === datComponentData.datSelectedANSI
        );
        if (defaultAnsi) {
          setEncodingDropdownValue(defaultAnsi);
        } else if (ansiOptions.length > 0) {
          // Fallback to first option if datSelectedANSI not found
          setEncodingDropdownValue(ansiOptions[0]);
        }
      } else if (ansiOptions.length > 0) {
        // Fallback to first option if no datSelectedANSI
        setEncodingDropdownValue(ansiOptions[0]);
      }

      // Date Format options
      const dateOptions =
        datComponentData.dateFormats?.map((item) => ({
          id: item.value.toString(),
          name: item.text,
        })) || [];
      setDateFormatOptions(dateOptions);

      // Set initial date format selection based on modelData.dateFormatID
      if (datComponentData.modelData?.dateFormatID && dateOptions.length > 0) {
        const defaultDateFormat = dateOptions.find(
          (option) =>
            option.id === datComponentData.modelData.dateFormatID.toString()
        );
        if (defaultDateFormat) {
          setDateFormat(defaultDateFormat);
        } else if (dateOptions.length > 0) {
          // Fallback to first option if dateFormatID not found
          setDateFormat(dateOptions[0]);
        }
      } else if (dateOptions.length > 0) {
        // Fallback to first option if no dateFormatID
        setDateFormat(dateOptions[0]);
      }

      // Field Separator options
      const fieldSeparatorOptions =
        datComponentData.columnDelimiterList?.map((item) => ({
          id: item.value,
          name: item.text,
        })) || [];
      setFieldSeparatorOptions(fieldSeparatorOptions);

      // Multi Value options
      const multiValueOptions =
        datComponentData.dataDelimiterList?.map((item) => ({
          id: item.value,
          name: item.text,
        })) || [];
      setMultiValueOptions(multiValueOptions);

      // Text Qualifier options
      const textQualifierOptions =
        datComponentData.textQualifierList?.map((item) => ({
          id: item.value,
          name: item.text,
        })) || [];
      setTextQualifierOptions(textQualifierOptions);

      // New Line options
      const newLineOptions =
        datComponentData.multiline?.map((item) => ({
          id: item.value,
          name: item.text,
        })) || [];
      setNewLineOptions(newLineOptions);

      // Set initial field separator selection based on modelData.fieldSeparator
      if (
        datComponentData.modelData?.fieldSeparator !== undefined &&
        fieldSeparatorOptions.length > 0
      ) {
        const defaultFieldSeparator = fieldSeparatorOptions.find(
          (option) =>
            option.id === datComponentData.modelData.fieldSeparator.toString()
        );
        if (defaultFieldSeparator) {
          setFieldSeparator(defaultFieldSeparator);
        } else if (fieldSeparatorOptions.length > 0) {
          setFieldSeparator(fieldSeparatorOptions[0]);
        }
      } else if (fieldSeparatorOptions.length > 0) {
        setFieldSeparator(fieldSeparatorOptions[0]);
      }

      // Set initial multi value selection based on modelData.multiValue
      if (
        datComponentData.modelData?.multiValue !== undefined &&
        multiValueOptions.length > 0
      ) {
        const defaultMultiValue = multiValueOptions.find(
          (option) =>
            option.id === datComponentData.modelData.multiValue.toString()
        );
        if (defaultMultiValue) {
          setMultiValue(defaultMultiValue);
        } else if (multiValueOptions.length > 0) {
          setMultiValue(multiValueOptions[0]);
        }
      } else if (multiValueOptions.length > 0) {
        setMultiValue(multiValueOptions[0]);
      }

      // Set initial text qualifier selection based on modelData.textQualifier
      if (
        datComponentData.modelData?.textQualifier !== undefined &&
        textQualifierOptions.length > 0
      ) {
        const defaultTextQualifier = textQualifierOptions.find(
          (option) =>
            option.id === datComponentData.modelData.textQualifier.toString()
        );
        if (defaultTextQualifier) {
          setTextQualifier(defaultTextQualifier);
        } else if (textQualifierOptions.length > 0) {
          setTextQualifier(textQualifierOptions[0]);
        }
      } else if (textQualifierOptions.length > 0) {
        setTextQualifier(textQualifierOptions[0]);
      }

      // Set initial new line selection based on modelData.newLineSeparator
      if (
        datComponentData.modelData?.newLineSeparator !== undefined &&
        newLineOptions.length > 0
      ) {
        const defaultNewLine = newLineOptions.find(
          (option) =>
            option.id === datComponentData.modelData.newLineSeparator.toString()
        );
        if (defaultNewLine) {
          setNewLine(defaultNewLine);
        } else if (newLineOptions.length > 0) {
          setNewLine(newLineOptions[0]);
        }
      } else if (newLineOptions.length > 0) {
        setNewLine(newLineOptions[0]);
      }

      // Category options are static - no need to set from API

      // Populate fields from datFieldMappingDataList
      if (
        datComponentData.modelData?.datFieldMappingDataList &&
        datComponentData.modelData.datFieldMappingDataList.length > 0
      ) {
        const mappedFields: DATField[] =
          datComponentData.modelData.datFieldMappingDataList.map(
            (item, index) => {
              const isTagsCategory =
                item.selectedTypeId?.toLowerCase() === 'tags';
              let selectedTags: {id: string; label: string}[] = [];

              // If it's a tags category and selectedSourceId contains comma-separated tag IDs
              if (isTagsCategory && item.selectedSourceId) {
                const tagIds = item.selectedSourceId
                  .split(',')
                  .map((id) => id.trim());

                // Only process tags if we have tag tree data available
                if (tagTreeData && tagTreeData.length > 0) {
                  // Flatten the tag tree to find matching tags
                  const flattenTree = (
                    nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
                  ): ExtendedTreeItemProps[] => {
                    return nodes.reduce<ExtendedTreeItemProps[]>(
                      (acc, node) => {
                        acc.push(node);
                        if (node.children)
                          acc.push(...flattenTree(node.children));
                        return acc;
                      },
                      []
                    );
                  };

                  const flatTree = flattenTree(tagTreeData);

                  selectedTags = flatTree
                    .filter((node) => tagIds.includes(node.id))
                    .map((node) => ({id: node.id, label: node.label}));
                } else {
                  selectedTags = [];
                }
              }

              return {
                id: index + 1,
                category: isTagsCategory ? 'Tags' : 'Field',
                sourceField: item.selectedSourceId,
                datFieldHeaderName: item.datField,
                scrubRedactions: item.isRedacted,
                scrubPriv: item.isPrivileged,
                selectedTags: selectedTags,
              };
            }
          );
        setFields(mappedFields);
      } else {
        // Fallback to default field if no API data
        // Find DocID from grouped field options for EXPORT
        let docIdFieldId = '';
        if (
          moduleType === ModuleType.EXPORT &&
          groupedFieldOptions.length > 0
        ) {
          const docIdField = groupedFieldOptions
            .flatMap((group) => group.items)
            .find((item) => item.label === 'DocID');
          if (docIdField) {
            docIdFieldId = docIdField.id;
          }
        }
        setFields([
          {
            id: 1,
            category: 'Field',
            sourceField:
              moduleType === ModuleType.EXPORT ? docIdFieldId : '-146',
            datFieldHeaderName:
              moduleType === ModuleType.EXPORT ? 'DocID' : 'ProdBeginningBates',
            scrubRedactions: false,
            scrubPriv: false,
            selectedTags: [] as {id: string; label: string}[],
          },
        ]);
      }

      // Grouped field options from datTypeList (filter out TAGS typeId)
      const groupedOptions =
        datComponentData.modelData?.datTypeList
          ?.filter((type) => type.typeId !== 'TAGS')
          ?.map((type) => ({
            groupName: type.typeLabel,
            items:
              type.datSourceTypeList?.map((source) => ({
                id: source.value,
                label: source.text,
              })) || [],
          })) || [];
      setGroupedFieldOptions(groupedOptions);
    }
  }, [datComponentData]);

  // Set default DocID field when grouped field options load for EXPORT
  useEffect(() => {
    if (
      moduleType === ModuleType.EXPORT &&
      groupedFieldOptions.length > 0 &&
      fields.length > 0 &&
      // Only apply this defaulting logic when there is no existing mapping
      !datComponentData?.modelData?.datFieldMappingDataList?.length
    ) {
      // Find DocID from grouped field options
      const docIdField = groupedFieldOptions
        .flatMap((group) => group.items)
        .find((item) => item.label === 'DocID');

      if (docIdField) {
        setFields((currentFields) => {
          // Update fields that have empty sourceField or ProdBeginningBates to use DocID
          const updatedFields = currentFields.map((field) => {
            if (
              field.category === 'Field' &&
              (field.sourceField === '' ||
                field.sourceField === '-146' ||
                field.datFieldHeaderName === 'ProdBeginningBates' ||
                (field.datFieldHeaderName === 'DocID' &&
                  field.sourceField === ''))
            ) {
              return {
                ...field,
                sourceField: docIdField.id,
                datFieldHeaderName: 'DocID',
              };
            }
            return field;
          });
          // Only update if there are actual changes
          const hasChanges = updatedFields.some(
            (field, index) =>
              field.sourceField !== currentFields[index]?.sourceField ||
              field.datFieldHeaderName !==
                currentFields[index]?.datFieldHeaderName
          );
          return hasChanges ? updatedFields : currentFields;
        });
      }
    }
  }, [groupedFieldOptions, moduleType]);

  // Re-process fields when tagTreeData becomes available
  useEffect(() => {
    if (
      datComponentData?.modelData?.datFieldMappingDataList &&
      tagTreeData &&
      tagTreeData.length > 0
    ) {
      setFields((currentFields) => {
        const updatedFields = currentFields.map((field) => {
          // Only process tags category fields that don't have selectedTags yet
          if (
            field.category === 'Tags' &&
            field.sourceField &&
            (!field.selectedTags || field.selectedTags.length === 0)
          ) {
            const tagIds = field.sourceField.split(',').map((id) => id.trim());

            // Flatten the tag tree to find matching tags
            const flattenTree = (
              nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
            ): ExtendedTreeItemProps[] => {
              return nodes.reduce<ExtendedTreeItemProps[]>((acc, node) => {
                acc.push(node);
                if (node.children) acc.push(...flattenTree(node.children));
                return acc;
              }, []);
            };

            const flatTree = flattenTree(tagTreeData);
            const selectedTags = flatTree
              .filter((node) => tagIds.includes(node.id))
              .map((node) => ({id: node.id, label: node.label}));

            return {
              ...field,
              selectedTags: selectedTags,
            };
          }
          return field;
        });

        // Only update if there are changes
        const hasChanges = updatedFields.some(
          (field, index) =>
            field.selectedTags?.length !==
            currentFields[index]?.selectedTags?.length
        );

        return hasChanges ? updatedFields : currentFields;
      });
    }
  }, [tagTreeData, datComponentData]);

  const [encoding, setEncoding] = useState('rdbANSI');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<number | null>(null);

  const [encodingDropdownValue, setEncodingDropdownValue] =
    useState<ISelectItem | null>(null);
  const [dateFormat, setDateFormat] = useState<ISelectItem | null>(null);
  const [fieldSeparator, setFieldSeparator] = useState<ISelectItem | null>(
    null
  );
  const [multiValue, setMultiValue] = useState<ISelectItem | null>(null);
  const [textQualifier, setTextQualifier] = useState<ISelectItem | null>(null);
  const [newLine, setNewLine] = useState<ISelectItem | null>(null);

  const {
    control,
    formState: {isDirty},
  } = useForm({
    defaultValues: {
      encoding,
      encodingDropdownValue,
      dateFormat,
      fieldSeparator,
      multiValue,
      textQualifier,
      newLine,
      formDirty: false,
    },
  });
  // Determine default field based on moduleType
  const getDefaultField = () => {
    if (moduleType === ModuleType.EXPORT) {
      // For EXPORT, use DocID as default (will be set when options load)
      return {
        id: Date.now(),
        category: 'Field',
        sourceField: '', // Will be set to DocID when options load
        datFieldHeaderName: 'DocID',
        scrubRedactions: false,
        scrubPriv: false,
        selectedTags: [] as {id: string; label: string}[],
      };
    } else {
      // For PRODUCTION, use ProdBeginningBates as default
      return {
        id: Date.now(),
        category: 'Field',
        sourceField: '-146',
        datFieldHeaderName: 'ProdBeginningBates',
        scrubRedactions: false,
        scrubPriv: false,
        selectedTags: [] as {id: string; label: string}[],
      };
    }
  };

  const [fields, setFields] = useState<DATField[]>([getDefaultField()]);

  // Notify parent component when fields change
  useEffect(() => {
    if (onFieldsChange) {
      // Use setTimeout to ensure the form validation runs after the fields are updated
      setTimeout(() => {
        onFieldsChange(fields);
      }, 0);
    }
  }, [fields, onFieldsChange]);

  useEffect(() => {
    if (isDirty || componentIsDirty) {
      dispatch(setIsFieldsChanged({isFieldsChanged: true}));
    }
  }, [isDirty, componentIsDirty]);
  useEffect(() => {
    if (!isViewMode) {
      return;
    }

    let animationFrameId = 0;
    const timeoutIds: number[] = [];

    const getControlText = (element: HTMLElement): string =>
      (element.getAttribute('aria-label') || element.textContent || '')
        .trim()
        .replace(/\s+/g, ' ');

    const getDropdownDisplayValue = (element: HTMLElement): string =>
      (element.textContent || element.getAttribute('aria-label') || '')
        .replace(/\s+Selected$/i, '')
        .trim()
        .replace(/\s+/g, ' ');

    const setViewModeFocusable = (
      element: HTMLElement | null | undefined,
      ariaLabel?: string
    ) => {
      if (!element) {
        return;
      }

      if (element.getAttribute('tabindex') !== '0') {
        element.setAttribute('tabindex', '0');
      }

      if (element.getAttribute('aria-disabled') !== 'true') {
        element.setAttribute('aria-disabled', 'true');
      }

      if (ariaLabel && element.getAttribute('aria-label') !== ariaLabel) {
        element.setAttribute('aria-label', ariaLabel);
      }

      const hasExistingFocusHandler =
        datViewModeFocusRingTargetsRef.current.some(
          (entry) => entry.element === element
        );

      if (!hasExistingFocusHandler) {
        const focusHandler = () => {
          element.style.outline = '2px solid #fff';
          element.style.outlineOffset = '2px';
          element.style.borderRadius = element.style.borderRadius || '4px';
        };
        const blurHandler = () => {
          element.style.outline = '';
          element.style.outlineOffset = '';
        };

        element.addEventListener('focus', focusHandler);
        element.addEventListener('blur', blurHandler);
        datViewModeFocusRingTargetsRef.current.push({
          element,
          focusHandler,
          blurHandler,
        });
      }
    };

    const setViewModeNonFocusable = (
      element: HTMLElement | null | undefined
    ) => {
      if (!element) {
        return;
      }

      if (element.getAttribute('tabindex') !== '-1') {
        element.setAttribute('tabindex', '-1');
      }
    };

    const isAnsiDropdownSkipped = () => encoding !== 'rdbANSI';

    const isDateDropdownSkipped = () => {
      const container = datComponentRef.current;
      const dateTooltip = container?.querySelector<HTMLElement>(
        '[data-testid="selected-item-tooltip-asd-lstDateFormat"]'
      );

      return (
        !!dateTooltip &&
        getDropdownDisplayValue(dateTooltip) === 'YYYY/MM/DD HH:MI:SS'
      );
    };

    const isSkippedDropdownElement = (element: HTMLElement): boolean => {
      const skippedAnsiSelector =
        '[data-testid="wrapper-asd-elstANSIType"], [data-testid="arr-select-dropdown-asd-elstANSIType"], [data-testid="asd-elstANSIType"], [data-testid="selected-item-tooltip-asd-elstANSIType"], [data-testid="asd-elstANSIType-input-field"]';
      const skippedDateSelector =
        '[data-testid="wrapper-asd-lstDateFormat"], [data-testid="arr-select-dropdown-asd-lstDateFormat"], [data-testid="asd-lstDateFormat"], [data-testid="selected-item-tooltip-asd-lstDateFormat"], [data-testid="asd-lstDateFormat-input-field"]';

      return (
        (isAnsiDropdownSkipped() &&
          !!element.closest<HTMLElement>(skippedAnsiSelector)) ||
        (isDateDropdownSkipped() &&
          !!element.closest<HTMLElement>(skippedDateSelector))
      );
    };

    const getVisibleFocusableElements = (): HTMLElement[] => {
      const container = datComponentRef.current;
      if (!container) {
        return [];
      }

      return Array.from(
        container.querySelectorAll<HTMLElement>(
          'button, input, select, textarea, a[href], [role="button"], [role="radio"], [role="checkbox"], [role="combobox"], [tabindex="0"]'
        )
      ).filter((element) => {
        if (element.getAttribute('tabindex') === '-1') {
          return false;
        }

        if (isSkippedDropdownElement(element)) {
          return false;
        }

        const style = window.getComputedStyle(element);
        return style.display !== 'none' && style.visibility !== 'hidden';
      });
    };

    const focusAdjacentByDomOrder = (fromElement: HTMLElement) => {
      const focusableElements = getVisibleFocusableElements();
      const targetElement = datLastTabWasShiftRef.current
        ? [...focusableElements]
            .reverse()
            .find(
              (element) =>
                !!(
                  fromElement.compareDocumentPosition(element) &
                  Node.DOCUMENT_POSITION_PRECEDING
                )
            )
        : focusableElements.find(
            (element) =>
              !!(
                fromElement.compareDocumentPosition(element) &
                Node.DOCUMENT_POSITION_FOLLOWING
              )
          );

      window.requestAnimationFrame(() => {
        targetElement?.focus();
      });
    };

    const addSkippedDropdownFocusHandler = (element: HTMLElement) => {
      const hasExistingHandler = datSkippedDropdownTargetsRef.current.some(
        (entry) => entry.element === element
      );

      if (hasExistingHandler) {
        return;
      }

      const focusHandler = (event: Event) => {
        event.preventDefault();
        element.blur();
        focusAdjacentByDomOrder(element);
      };

      element.addEventListener('focus', focusHandler);
      datSkippedDropdownTargetsRef.current.push({element, focusHandler});
    };

    const markSkippedDropdownNonFocusable = (element: HTMLElement | null) => {
      if (!element) {
        return;
      }

      setViewModeNonFocusable(element);
      element.setAttribute('aria-hidden', 'true');
      element.style.outline = 'none';
      element.style.boxShadow = 'none';
      addSkippedDropdownFocusHandler(element);
    };

    const applySkippedDropdownValues = () => {
      const container = datComponentRef.current;
      if (!container) {
        return;
      }

      if (isAnsiDropdownSkipped()) {
        [
          'wrapper-asd-elstANSIType',
          'arr-select-dropdown-asd-elstANSIType',
          'asd-elstANSIType',
          'selected-item-tooltip-asd-elstANSIType',
          'asd-elstANSIType-input-field',
        ].forEach((testId) => {
          markSkippedDropdownNonFocusable(
            container.querySelector<HTMLElement>(`[data-testid="${testId}"]`)
          );
        });
      }

      if (isDateDropdownSkipped()) {
        [
          'wrapper-asd-lstDateFormat',
          'arr-select-dropdown-asd-lstDateFormat',
          'asd-lstDateFormat',
          'selected-item-tooltip-asd-lstDateFormat',
          'asd-lstDateFormat-input-field',
        ].forEach((testId) => {
          markSkippedDropdownNonFocusable(
            container.querySelector<HTMLElement>(`[data-testid="${testId}"]`)
          );
        });
      }
    };

    const getDropdownLabelFromSelectedTooltip = (
      selectedTooltipElement: HTMLElement
    ): string => {
      const selectedText = getDropdownDisplayValue(selectedTooltipElement);
      const dropdownDisplayElement =
        selectedTooltipElement.closest<HTMLElement>(
          '[role="combobox"][data-testid]'
        );
      const dropdownId =
        dropdownDisplayElement?.getAttribute('data-testid') || '';

      if (dropdownId.includes('asd-elstANSIType')) {
        return `${t(
          'productionsAndExports.createNewProduction.datComponent.selectAnsi'
        )} ${selectedText}`;
      }

      if (dropdownId.includes('asd-lstDateFormat')) {
        return `${t(
          'productionsAndExports.createNewProduction.datComponent.dateFormat'
        )} ${selectedText}`;
      }

      if (dropdownId.includes('asd-lstFieldSeparator')) {
        return `${t(
          'productionsAndExports.createNewProduction.datComponent.fieldSeparator'
        )} ${selectedText}`;
      }

      if (dropdownId.includes('asd-lstMultiValue')) {
        return `${t(
          'productionsAndExports.createNewProduction.datComponent.multiValue'
        )} ${selectedText}`;
      }

      if (dropdownId.includes('asd-lstTextQualifier')) {
        return `${t(
          'productionsAndExports.createNewProduction.datComponent.textQualifier'
        )} ${selectedText}`;
      }

      if (dropdownId.includes('asd-lstNewLineSeparator')) {
        return `${t(
          'productionsAndExports.createNewProduction.datComponent.newLine'
        )} ${selectedText}`;
      }

      return selectedText;
    };

    const applyDatViewModeFocus = () => {
      const container = datComponentRef.current;
      if (!container) {
        return;
      }

      const focusableElements = container.querySelectorAll<HTMLElement>(
        'button, input, select, textarea, a[href], [role="button"], [role="radio"], [role="checkbox"], [role="combobox"], [contenteditable="true"], [tabindex]:not([tabindex="-1"])'
      );

      focusableElements.forEach((element) => {
        const isInfoIcon =
          element.classList.contains('dat-info-icon') &&
          element.getAttribute('role') === 'button';
        const isSelectedDropdownValue = element.matches(
          '[data-testid^="selected-item-tooltip-"]'
        );

        if (isInfoIcon) {
          setViewModeFocusable(element);
          return;
        }

        if (isSkippedDropdownElement(element)) {
          markSkippedDropdownNonFocusable(element);
          return;
        }

        if (isSelectedDropdownValue) {
          setViewModeFocusable(
            element,
            getDropdownLabelFromSelectedTooltip(element)
          );
          return;
        }

        setViewModeNonFocusable(element);
      });

      container
        .querySelectorAll<
          HTMLInputElement | HTMLTextAreaElement
        >('input:not([type="hidden"]), textarea')
        .forEach((inputElement) => {
          if (isSkippedDropdownElement(inputElement)) {
            markSkippedDropdownNonFocusable(inputElement);
            return;
          }

          const inputType = inputElement.getAttribute('type');
          if (
            inputType === 'radio' ||
            inputType === 'checkbox' ||
            inputType === 'file'
          ) {
            return;
          }

          const hasValue = String(inputElement.value ?? '').trim().length > 0;
          if (!hasValue) {
            return;
          }

          const inputFocusTarget =
            inputElement.closest<HTMLElement>('.MuiFormControl-root') ??
            inputElement.closest<HTMLElement>('.MuiInputBase-root') ??
            inputElement.closest<HTMLElement>('[data-testid]') ??
            inputElement.parentElement ??
            inputElement;

          if (inputFocusTarget && isSkippedDropdownElement(inputFocusTarget)) {
            markSkippedDropdownNonFocusable(inputFocusTarget);
            return;
          }

          setViewModeFocusable(
            inputFocusTarget,
            inputElement.getAttribute('aria-label') ?? undefined
          );
        });

      container
        .querySelectorAll<HTMLInputElement>('input[type="radio"]')
        .forEach((radioElement) => {
          if (!radioElement.checked) {
            return;
          }

          const radioFocusTarget =
            radioElement.closest<HTMLElement>('label') ??
            radioElement.closest<HTMLElement>('[data-testid]') ??
            radioElement;

          setViewModeFocusable(
            radioFocusTarget,
            getControlText(radioFocusTarget)
          );
        });

      container
        .querySelectorAll<HTMLInputElement>('input[type="checkbox"]')
        .forEach((checkboxElement) => {
          if (!checkboxElement.checked) {
            return;
          }

          const checkboxFocusTarget =
            checkboxElement.closest<HTMLElement>('label') ??
            checkboxElement.closest<HTMLElement>('[data-testid]') ??
            checkboxElement;

          setViewModeFocusable(
            checkboxFocusTarget,
            getControlText(checkboxFocusTarget)
          );
        });

      container
        .querySelectorAll<HTMLElement>('[role="combobox"][data-testid]')
        .forEach((comboboxElement) => {
          if (isSkippedDropdownElement(comboboxElement)) {
            markSkippedDropdownNonFocusable(comboboxElement);
            return;
          }

          const hasSelectedTooltip = comboboxElement.querySelector<HTMLElement>(
            '[data-testid^="selected-item-tooltip-"]'
          );
          if (hasSelectedTooltip) {
            setViewModeNonFocusable(comboboxElement);
            return;
          }

          const selectedText = getControlText(comboboxElement);
          if (!selectedText) {
            setViewModeNonFocusable(comboboxElement);
            return;
          }

          setViewModeFocusable(comboboxElement, selectedText);
        });

      container
        .querySelectorAll<HTMLElement>('[data-testid^="tags-source-field-"]')
        .forEach((chipElement) => {
          const chipText = getControlText(chipElement);
          if (!chipText) {
            return;
          }

          chipElement
            .querySelectorAll<HTMLElement>(
              'button, [role="button"], [tabindex]'
            )
            .forEach((nestedElement) => {
              if (nestedElement !== chipElement) {
                setViewModeNonFocusable(nestedElement);
              }
            });

          setViewModeFocusable(chipElement, chipText);
        });

      applySkippedDropdownValues();
    };

    const scheduleFocusUpdate = () => {
      window.cancelAnimationFrame(animationFrameId);
      animationFrameId = window.requestAnimationFrame(applyDatViewModeFocus);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        datLastTabWasShiftRef.current = event.shiftKey;
      }
    };

    const handleFocusIn = (event: FocusEvent) => {
      if (!(event.target instanceof HTMLElement)) {
        return;
      }

      const skippedFocusTarget = event.target.closest<HTMLElement>(
        '[data-testid="wrapper-asd-elstANSIType"], [data-testid="arr-select-dropdown-asd-elstANSIType"], [data-testid="asd-elstANSIType"], [data-testid="selected-item-tooltip-asd-elstANSIType"], [data-testid="wrapper-asd-lstDateFormat"], [data-testid="arr-select-dropdown-asd-lstDateFormat"], [data-testid="asd-lstDateFormat"], [data-testid="selected-item-tooltip-asd-lstDateFormat"]'
      );

      if (skippedFocusTarget && isSkippedDropdownElement(skippedFocusTarget)) {
        event.preventDefault();
        skippedFocusTarget.blur();
        focusAdjacentByDomOrder(skippedFocusTarget);
      }
    };

    scheduleFocusUpdate();
    [0, 25, 50, 100, 250, 500, 1000].forEach((delay) => {
      timeoutIds.push(window.setTimeout(applySkippedDropdownValues, delay));
    });

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('focusin', handleFocusIn, true);

    const observer = new MutationObserver(scheduleFocusUpdate);
    if (datComponentRef.current) {
      observer.observe(datComponentRef.current, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['tabindex', 'disabled', 'aria-disabled', 'value'],
      });
    }

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
      observer.disconnect();
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('focusin', handleFocusIn, true);
      datSkippedDropdownTargetsRef.current.forEach(
        ({element, focusHandler}) => {
          element.removeEventListener('focus', focusHandler);
        }
      );
      datSkippedDropdownTargetsRef.current = [];
      datViewModeFocusRingTargetsRef.current.forEach(
        ({element, focusHandler, blurHandler}) => {
          element.removeEventListener('focus', focusHandler);
          element.removeEventListener('blur', blurHandler);
          element.style.outline = '';
          element.style.outlineOffset = '';
        }
      );
      datViewModeFocusRingTargetsRef.current = [];
    };
  }, [
    isViewMode,
    fields,
    encoding,
    encodingDropdownValue,
    dateFormat,
    fieldSeparator,
    multiValue,
    textQualifier,
    newLine,
  ]);

  // State for tag panel
  const [isTagPanelOpen, setIsTagPanelOpen] = useState(false);
  const [selectedTags, setSelectedTags] = useState<
    TreeViewBaseItem<ExtendedTreeItemProps>[]
  >([]);
  const [currentFieldId, setCurrentFieldId] = useState<number | null>(null);

  // Handle opening tag panel for a specific field
  const handleOpenTagPanel = (fieldId: number) => {
    setCurrentFieldId(fieldId);
    setComponentIsDirty(true);
    const field = fields.find((f) => f.id === fieldId);
    if (field) {
      // Find matching tags in the tree data to preserve full structure
      const preSelectedTags: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];

      if (field.selectedTags && field.selectedTags.length > 0) {
        // Flatten the tree to find matching tags
        const flattenTree = (
          nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
        ): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
          return nodes.reduce<TreeViewBaseItem<ExtendedTreeItemProps>[]>(
            (acc, node) => {
              acc.push(node);
              if (node.children) acc.push(...flattenTree(node.children));
              return acc;
            },
            []
          );
        };

        const flatTree = flattenTree(tagTreeData);

        // Find matching tags in the tree and add them to preSelectedTags
        field.selectedTags.forEach((selectedTag) => {
          const matchingTreeTag = flatTree.find(
            (treeTag) => treeTag.id === selectedTag.id
          );
          if (matchingTreeTag) {
            preSelectedTags.push(matchingTreeTag);
          }
        });
      }

      setSelectedTags(preSelectedTags);
      setIsTagPanelOpen(true);
    } else {
      setSelectedTags([]);
      setIsTagPanelOpen(true);
    }
  };

  // Handle tag selection from panel
  const handleTagSelection = (
    tags: TreeViewBaseItem<ExtendedTreeItemProps>[]
  ) => {
    if (currentFieldId !== null) {
      // Filter out parent tags and only keep child tags (tags without children)
      const childTags = tags.filter(
        (tag) => !tag.children || tag.children.length === 0
      );

      // Convert tree format back to simple format
      const simpleTags = childTags.map((tag) => ({
        id: tag.id,
        label: tag.label,
      }));

      // Update the specific field with selected tags and set header name to "Tags"
      setFields((prev) =>
        prev.map((field) =>
          field.id === currentFieldId
            ? {
                ...field,
                selectedTags: simpleTags,
                datFieldHeaderName:
                  simpleTags.length > 0 ? 'Tags' : field.datFieldHeaderName,
              }
            : field
        )
      );
    }
    setIsTagPanelOpen(false);
    setCurrentFieldId(null);
  };

  const addField = useCallback(() => {
    setFields((prev) => {
      const isFirstRow = prev.length === 0;
      setComponentIsDirty(true);

      // When the grid is empty and a new row is added, default to
      // ProdBeginningBates for PRODUCTION and DocID for EXPORT.
      if (isFirstRow) {
        // Try to resolve DocID from grouped options for EXPORT
        let defaultSourceField = '';
        let defaultHeaderName = '';

        if (moduleType === ModuleType.EXPORT) {
          const docIdField = groupedFieldOptions
            .flatMap((group) => group.items)
            .find((item) => item.label === 'DocID');
          defaultSourceField = docIdField ? docIdField.id : '';
          defaultHeaderName = docIdField ? docIdField.label : 'DocID';
        } else {
          // PRODUCTION defaults
          defaultSourceField = '-146';
          defaultHeaderName = 'ProdBeginningBates';
        }

        return [
          {
            id: 1,
            category: 'Field',
            sourceField: defaultSourceField,
            datFieldHeaderName: defaultHeaderName,
            scrubRedactions: false,
            scrubPriv: false,
            selectedTags: [] as {id: string; label: string}[],
          },
        ];
      }

      // For additional rows, keep them empty by default
      const nextId =
        prev.reduce((maxId, field) => Math.max(maxId, field.id), 0) + 1;
      return [
        ...prev,
        {
          id: nextId,
          category: 'Field',
          sourceField: '',
          datFieldHeaderName: '',
          scrubRedactions: false,
          scrubPriv: false,
        },
      ];
    });
  }, [groupedFieldOptions, moduleType]);

  const updateField = useCallback(
    (id: number, key: keyof DATField, value: any) => {
      setFields((prev) =>
        prev.map((f) => (f.id === id ? {...f, [key]: value} : f))
      );
    },
    []
  );

  const removeField = (id: number) => {
    setFields((prev) => prev.filter((f) => f.id !== id));
  };

  // Show loading state
  if (isDatComponentLoading || datComponentError || !datComponentData) {
    return <ArrLoadingSpinner />;
  }

  return (
    <>
      <Box className="dat-component" ref={datComponentRef}>
        {/* Grid layout */}
        <Box className="dat-grid">
          {/* Encoding Section */}
          <Box className="dat-grid-encoding">
            <Box className="dat-grid-encoding-header">
              <Typography
                id="encoding-label"
                className="typo-text label2_size"
                data-testid={`dat-component-encoding-encoding-tooltip`}
              >
                {t(
                  'productionsAndExports.createNewProduction.datComponent.encoding'
                )}
                <ArrTooltipWrapper
                  closeTooltipOnEscPress
                  trigger={
                    <InfoOutlinedIcon
                      className="dat-info-icon"
                      {...getDatTooltipIconA11yProps(
                        `${t('productionsAndExports.createNewProduction.datComponent.encoding')} info`
                      )}
                    />
                  }
                >
                  {t(
                    'productionsAndExports.createNewProduction.datComponent.encodingTooltip'
                  )}
                </ArrTooltipWrapper>
              </Typography>
            </Box>

            <Box
              className="dat-radio-group"
              role="radiogroup"
              aria-labelledby="encoding-label"
            >
              <Controller
                control={control}
                name="encoding"
                render={({field}) => (
                  <ArrRadio
                    dataTestId="encoding-field-1"
                    name="encoding"
                    value={encoding === 'rdbANSI' ? 'rdbANSI' : null}
                    onChange={(val: any) => {
                      field.onChange(val);
                      setEncoding(val);
                      setComponentIsDirty(true);
                    }}
                    data={[
                      {
                        label: t(
                          'productionsAndExports.createNewProduction.datComponent.ansiLabel'
                        ),
                        value: 'rdbANSI',
                      },
                    ]}
                    size="small"
                    {...viewModeDisabledA11yProps}
                    disabled={isViewMode}
                  />
                )}
              />

              <Controller
                control={control}
                name="encodingDropdownValue"
                render={({field}) => (
                  <ArrSingleSelectDropdown
                    dropdownLabel={t(
                      'productionsAndExports.createNewProduction.datComponent.selectAnsi'
                    )}
                    dropdownItems={ansiFormatOptions}
                    selectedValues={field.value || encodingDropdownValue}
                    onSelectionChange={(item) => {
                      if (isViewMode) return;
                      setComponentIsDirty(true);
                      item &&
                        (field.onChange(item), setEncodingDropdownValue(item));
                    }}
                    sizeVariant="medium"
                    {...viewModeDisabledA11yProps}
                    disabled={encoding === 'lstANSIType' || isViewMode}
                    dataTestId="asd-elstANSIType"
                    searchable
                    showNoResultsText
                  />
                )}
              />

              <Controller
                control={control}
                name="encoding"
                render={({field}) => (
                  <ArrRadio
                    dataTestId="encoding-field-2"
                    name="encoding"
                    value={encoding}
                    aria-checked={encoding === 'lstANSIType'}
                    onChange={(val: any) => {
                      setEncoding(val);
                      field.onChange(val);
                      setComponentIsDirty(true);
                    }}
                    data={[
                      {
                        label: t(
                          'productionsAndExports.createNewProduction.datComponent.unicodeLabel'
                        ),
                        value: 'lstANSIType',
                      },
                    ]}
                    size="small"
                    {...viewModeDisabledA11yProps}
                    disabled={isViewMode}
                  />
                )}
              />
            </Box>
          </Box>

          {/* Date Format Section */}
          <Box className="dat-grid-dateformat">
            <Box className="dat-grid-date-header">
              <Typography
                className="typo-text label2_size"
                data-testid={`dat-component-date-format-date-format-tooltip`}
              >
                {t(
                  'productionsAndExports.createNewProduction.datComponent.dateFormat'
                )}
                <ArrTooltipWrapper
                  closeTooltipOnEscPress
                  trigger={
                    <InfoOutlinedIcon
                      className="dat-info-icon"
                      {...getDatTooltipIconA11yProps(
                        `${t('productionsAndExports.createNewProduction.datComponent.dateFormat')} info`
                      )}
                    />
                  }
                >
                  {t(
                    'productionsAndExports.createNewProduction.datComponent.dateFormatTooltip'
                  )}
                </ArrTooltipWrapper>
              </Typography>
            </Box>
            <Box className="dat-date-format">
              <Controller
                control={control}
                name="dateFormat"
                render={({field}) => (
                  <ArrSingleSelectDropdown
                    dropdownLabel="select format"
                    dropdownItems={dateFormatOptions}
                    selectedValues={field.value || dateFormat || null}
                    onSelectionChange={(item) => {
                      if (isViewMode) return;
                      item && (field.onChange(item), setDateFormat(item));
                      setComponentIsDirty(true);
                    }}
                    sizeVariant="medium"
                    dataTestId="asd-lstDateFormat"
                    {...viewModeDisabledA11yProps}
                    disabled={isViewMode}
                    searchable
                    showNoResultsText
                  />
                )}
              />
            </Box>
          </Box>

          {/* Field Delimiters */}
          <Box className="dat-grid-field-delimiters-header">
            <Typography
              className="typo-text label2_size"
              data-testid={`dat-component-field-delimiters`}
            >
              {t(
                'productionsAndExports.createNewProduction.datComponent.fieldDelimiters'
              )}
              <ArrTooltipWrapper
                closeTooltipOnEscPress
                trigger={
                  <InfoOutlinedIcon
                    className="dat-info-icon"
                    {...getDatTooltipIconA11yProps(
                      `${t('productionsAndExports.createNewProduction.datComponent.fieldDelimiters')} info`
                    )}
                  />
                }
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html: t(
                      'productionsAndExports.createNewProduction.datComponent.fieldDelimitersTooltip'
                    ),
                  }}
                  data-testid={`dat-component-span`}
                />
              </ArrTooltipWrapper>
            </Typography>
          </Box>

          <Box className="dat-grid-field-delimiters">
            <Box className="dat-delimiter-inline">
              <span
                className="typo-text label2_size"
                data-testid={`dat-component-field-separator`}
              >
                {t(
                  'productionsAndExports.createNewProduction.datComponent.fieldSeparator'
                )}
              </span>
              <Controller
                control={control}
                name="fieldSeparator"
                render={({field}) => (
                  <ArrSingleSelectDropdown
                    dropdownLabel=""
                    dropdownItems={fieldSeparatorOptions}
                    selectedValues={field.value || fieldSeparator || null}
                    onSelectionChange={(item) => {
                      if (isViewMode) return;
                      item && (field.onChange(item), setFieldSeparator(item));
                    }}
                    sizeVariant="medium"
                    dataTestId="asd-lstFieldSeparator"
                    {...viewModeDisabledA11yProps}
                    disabled={isViewMode}
                    searchable
                    showNoResultsText
                  />
                )}
              />
            </Box>

            <Box className="dat-delimiter-inline">
              <span
                className="typo-text label2_size"
                data-testid={`dat-component-multi-value`}
              >
                {t(
                  'productionsAndExports.createNewProduction.datComponent.multiValue'
                )}
              </span>
              <Controller
                control={control}
                name="multiValue"
                render={({field}) => (
                  <ArrSingleSelectDropdown
                    dropdownLabel=""
                    dropdownItems={multiValueOptions}
                    selectedValues={field.value || multiValue || null}
                    onSelectionChange={(item) => {
                      if (isViewMode) return;
                      item && (field.onChange(item), setMultiValue(item));
                    }}
                    sizeVariant="medium"
                    dataTestId="asd-lstMultiValue"
                    /* DAT A11Y CHANGE: remove disabled VIEW-mode DAT control from tab sequence. */
                    {...viewModeDisabledA11yProps}
                    disabled={isViewMode}
                    searchable
                    showNoResultsText
                  />
                )}
              />
            </Box>

            <Box className="dat-delimiter-inline">
              <span
                className="typo-text label2_size"
                data-testid={`dat-component-text-qualifier`}
              >
                {t(
                  'productionsAndExports.createNewProduction.datComponent.textQualifier'
                )}
              </span>
              <Controller
                control={control}
                name="textQualifier"
                render={({field}) => (
                  <ArrSingleSelectDropdown
                    dropdownLabel=""
                    dropdownItems={textQualifierOptions}
                    selectedValues={field.value || textQualifier || null}
                    onSelectionChange={(item) => {
                      if (isViewMode) return;
                      item && (field.onChange(item), setTextQualifier(item));
                    }}
                    sizeVariant="medium"
                    dataTestId="asd-lstTextQualifier"
                    /* DAT A11Y CHANGE: remove disabled VIEW-mode DAT control from tab sequence. */
                    {...viewModeDisabledA11yProps}
                    disabled={isViewMode}
                    searchable
                    showNoResultsText
                  />
                )}
              />
            </Box>

            <Box className="dat-delimiter-inline">
              <span
                className="typo-text label2_size"
                data-testid={`dat-component-new-line`}
              >
                {t(
                  'productionsAndExports.createNewProduction.datComponent.newLine'
                )}
              </span>
              <Controller
                control={control}
                name="newLine"
                render={({field}) => (
                  <ArrSingleSelectDropdown
                    dropdownLabel=""
                    dropdownItems={newLineOptions}
                    selectedValues={field.value || newLine || null}
                    onSelectionChange={(item) => {
                      if (isViewMode) return;
                      item && (field.onChange(item), setNewLine(item));
                    }}
                    sizeVariant="medium"
                    dataTestId="asd-lstNewLineSeparator"
                    /* DAT A11Y CHANGE: remove disabled VIEW-mode DAT control from tab sequence. */
                    {...viewModeDisabledA11yProps}
                    disabled={isViewMode}
                    searchable
                    showNoResultsText
                  />
                )}
              />
            </Box>
          </Box>
        </Box>

        {/* DAT Field Mapping Grid */}
        <Box className="dat-field-mapping">
          <Typography
            className="typo-text subhead2_size"
            data-testid={`dat-component-dat-field-mapping`}
          >
            {t(
              'productionsAndExports.createNewProduction.datComponent.datFieldMapping'
            )}
            <ArrTooltipWrapper
              closeTooltipOnEscPress
              trigger={
                <InfoOutlinedIcon
                  className="dat-info-icon"
                  /* DAT A11Y CHANGE: keeps this info icon in the VIEW-mode focus order. */
                  {...getDatTooltipIconA11yProps(
                    `${t('productionsAndExports.createNewProduction.datComponent.datFieldMapping')} info`
                  )}
                />
              }
            >
              <span
                dangerouslySetInnerHTML={{
                  __html: t(
                    'productionsAndExports.createNewProduction.datComponent.datFieldMappingTooltip'
                  ),
                }}
                data-testid={`dat-component-span-text`}
              />
            </ArrTooltipWrapper>
          </Typography>

          <Box className="dat-grid-table" ref={datGridTableRef}>
            <ArrDataGridPro
              rows={fields}
              /* DAT A11Y CHANGE: keeps the DAT mapping grid wrapper out of VIEW-mode tab sequence. */
              {...viewModeDisabledA11yProps}
              rowReordering={!isViewMode}
              rowSelection={false}
              getRowHeight={() => 'auto'}
              autoHeight={true}
              getRowClassName={(params: any) =>
                params?.row?.category === 'Tags' &&
                Array.isArray(params?.row?.selectedTags) &&
                params.row.selectedTags.length > 0
                  ? 'dat-tags-row'
                  : ''
              }
              currentPage={1}
              showColumnVerticalBorder={false}
              disableColumnSorting={true}
              disableColumnFilter={true}
              slots={{rowReorderIcon: RowReorderIcon as any}}
              onSelectionStateChange={() => {}}
              disableVirtualization
              disableColumnReorder={true}
              onRowsReorder={setFields}
              columns={[
                {
                  field: 'category',
                  headerName: t(
                    'productionsAndExports.createNewProduction.datComponent.columns.category'
                  ),
                  flex: 0.7,
                  renderCell: (params) => (
                    <ArrSingleSelectDropdown
                      dropdownLabel="select"
                      dropdownItems={categoryOptions}
                      selectedValues={
                        categoryOptions.find(
                          (opt) => opt.id === params.row.category
                        ) || null
                      }
                      onSelectionChange={(opt) => {
                        if (isViewMode || !opt) return; // DAT A11Y CHANGE: defensive no-op for disabled VIEW-mode category dropdown.
                        setComponentIsDirty(true);
                        updateField(params.row.id, 'category', opt.id);
                        if (opt.id === 'Tags') {
                          updateField(
                            params.row.id,
                            'datFieldHeaderName',
                            'Tags'
                          );
                        } else if (opt.id === 'Field') {
                          updateField(params.row.id, 'datFieldHeaderName', '');
                          updateField(params.row.id, 'sourceField', '');
                        }
                      }}
                      sizeVariant="small"
                      dataTestId={`asd-hdnSequenceNumber_${params.row.id}`}
                      /* DAT A11Y CHANGE: remove disabled VIEW-mode DAT control from tab sequence. */
                      {...viewModeDisabledA11yProps}
                      disabled={isViewMode}
                    />
                  ),
                },
                {
                  field: 'sourceField',
                  headerName: t(
                    'productionsAndExports.createNewProduction.datComponent.columns.sourceField'
                  ),
                  flex: 0.9,
                  renderCell: (params: GridRenderCellParams) => {
                    const isTagsCategory = params.row.category === 'Tags';
                    return isTagsCategory ? (
                      <Box className="dat-grid-sourceField">
                        {!isViewMode && (
                          <ArrButton
                            variant="secondary"
                            size="small"
                            onClick={() => {
                              setComponentIsDirty(true);
                              handleOpenTagPanel(params.row.id);
                            }}
                            dataTestId={`SF_${params.row.id}`}
                          >
                            {t(
                              'productionsAndExports.createNewProduction.datComponent.selectTag'
                            )}
                          </ArrButton>
                        )}

                        {/* Display selected tags using ArrFilterChip styles */}
                        <Box className="arr-filter-chip-v1-selection-block">
                          {params.row.selectedTags?.map(
                            (tag: {id: string; label: string}) => (
                              <ArrFilterChip
                                dataTestId={`tags-source-field-${params.id}`}
                                key={tag.id}
                                filterName=""
                                selections={[{id: tag.id, value: tag.label}]}
                                variant="v1"
                                operator=""
                                isResponsive={false}
                                /* DAT A11Y CHANGE: remove disabled VIEW-mode DAT control from tab sequence. */
                                {...viewModeDisabledA11yProps}
                                disabled={isViewMode}
                                onRemove={() => {
                                  setComponentIsDirty(true);
                                  updateField(
                                    params.row.id,
                                    'selectedTags',
                                    params.row.selectedTags?.filter(
                                      (t: {id: string; label: string}) =>
                                        t.id !== tag.id
                                    )
                                  );
                                }}
                              />
                            )
                          )}
                        </Box>
                      </Box>
                    ) : (
                      <ArrGroupedSelectDropdown
                        dataTestId={`chips-source-${params.row.sourceField}-${params.id}`}
                        dropdownLabel="Select"
                        groupedItems={groupedFieldOptions}
                        selectedValue={params.row.sourceField}
                        onSelectionChange={(value) => {
                          if (isViewMode) return; // DAT A11Y CHANGE: defensive no-op for disabled VIEW-mode source field dropdown.
                          setComponentIsDirty(true);
                          updateField(params.row.id, 'sourceField', value);
                          // Auto-populate DAT Field Header Name with the display name of the selected field
                          const selectedField = groupedFieldOptions
                            .flatMap((group) => group.items)
                            .find((item) => item.id === value);
                          const displayName = selectedField
                            ? selectedField.label
                            : value;
                          updateField(
                            params.row.id,
                            'datFieldHeaderName',
                            displayName
                          );
                        }}
                        searchable={true}
                        sizeVariant="small"
                        data-testid={`select2-SF_${params.row.id}-container`}
                        /* DAT A11Y CHANGE: remove disabled VIEW-mode DAT control from tab sequence. */
                        {...viewModeDisabledA11yProps}
                        disabled={isViewMode}
                      />
                    );
                  },
                },
                {
                  field: 'datFieldHeaderName',
                  headerName: t(
                    'productionsAndExports.createNewProduction.datComponent.columns.datFieldHeaderName'
                  ),
                  flex: 0.9,
                  renderCell: (params) => (
                    <DatFieldHeaderNameCell
                      key={params.row.id}
                      rowId={params.row.id}
                      value={params.row.datFieldHeaderName}
                      isViewMode={isViewMode}
                      onChange={(rowId, value) => {
                        setComponentIsDirty(true);
                        updateField(rowId, 'datFieldHeaderName', value);
                      }}
                    />
                  ),
                },
                {
                  field: 'scrubRedactions',
                  headerName: t(
                    'productionsAndExports.createNewProduction.datComponent.columns.scrubRedactions'
                  ),
                  renderHeader: () => (
                    <span
                      className="scrubHeader"
                      data-testid={`dat-component-scrub-redactions-scrub-redactions-sub`}
                    >
                      <span data-testid={`dat-component-scrub-redactions`}>
                        {t(
                          'productionsAndExports.createNewProduction.datComponent.columns.scrubRedactions'
                        )}
                      </span>
                      <span
                        className="scrubTag"
                        data-testid={`dat-component-scrub-redactions-sub`}
                      >
                        {t(
                          'productionsAndExports.createNewProduction.datComponent.columns.scrubRedactionsSub'
                        )}
                      </span>
                    </span>
                  ),

                  flex: 0.8,
                  renderCell: (params) => (
                    <ArrCheckbox
                      checked={params.row.scrubRedactions || false}
                      onChange={(checked) => {
                        if (isViewMode) return; // DAT A11Y CHANGE: defensive no-op for disabled VIEW-mode redaction checkbox.
                        setComponentIsDirty(true);
                        updateField(params.row.id, 'scrubRedactions', checked);
                      }}
                      labelText=""
                      size="small"
                      id={`ChkRedacted_${params.row.id}`}
                      /* DAT A11Y CHANGE: remove disabled VIEW-mode DAT control from tab sequence. */
                      {...viewModeDisabledA11yProps}
                      disabled={isViewMode}
                      aria-label={`scrubRedactions-${params.row.id}`}
                    />
                  ),
                },
                {
                  field: 'scrubPriv',
                  headerName: t(
                    'productionsAndExports.createNewProduction.datComponent.columns.scrubRedactions'
                  ),
                  renderHeader: () => (
                    <span
                      className="scrubHeader"
                      data-testid={`dat-component-scrub-redactions-scrub-prev-sub`}
                    >
                      <span data-testid={`dat-component-scrub-redactions-text`}>
                        {t(
                          'productionsAndExports.createNewProduction.datComponent.columns.scrubRedactions'
                        )}
                      </span>
                      <span
                        className="scrubTag"
                        data-testid={`dat-component-scrub-prev-sub`}
                      >
                        {t(
                          'productionsAndExports.createNewProduction.datComponent.columns.scrubPrevSub'
                        )}
                      </span>
                    </span>
                  ),
                  flex: 0.6,
                  renderCell: (params) => (
                    <ArrCheckbox
                      checked={params.row.scrubPriv || false}
                      onChange={(checked) => {
                        if (isViewMode) return; // DAT A11Y CHANGE: defensive no-op for disabled VIEW-mode privilege-tag checkbox.
                        setComponentIsDirty(true);
                        updateField(params.row.id, 'scrubPriv', checked);
                      }}
                      labelText=""
                      size="small"
                      id={`ChkPrev_${params.row.id}`}
                      /* DAT A11Y CHANGE: remove disabled VIEW-mode DAT control from tab sequence. */
                      {...viewModeDisabledA11yProps}
                      disabled={isViewMode}
                      aria-label={`scrubPriv-${params.row.id}`}
                    />
                  ),
                },
                ...(!isViewMode
                  ? [
                      {
                        field: 'actions',
                        headerName: '',
                        flex: 0.1,
                        sortable: false,
                        filterable: false,
                        renderCell: (params: GridRenderCellParams) => (
                          <ArrButton
                            variant="text"
                            size="small"
                            onClick={() => {
                              setComponentIsDirty(true);
                              setRowToDelete(params.row.id);
                              setShowDeleteModal(true);
                            }}
                            startIcon={<DeleteOutlineOutlinedIcon />}
                            className="dat-delete-btn"
                            dataTestId={`dat-delete-btn-${params.id}`}
                          />
                        ),
                      },
                    ]
                  : []),
              ]}
            />

            {!isViewMode && (
              <Box className="dat-add-btn-container">
                <ArrButton
                  variant="primary"
                  size="medium"
                  onClick={addField}
                  className="dat-add-btn"
                  dataTestId="datAddFieldBtn"
                >
                  {t(
                    'productionsAndExports.createNewProduction.datComponent.addField'
                  )}
                </ArrButton>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      {showDeleteModal && (
        <ArrModal
          showModal={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          headerTitle="Delete"
          className="dat-delete-modal"
          title={t(
            'productionsAndExports.createNewProduction.datComponent.deleteMsg'
          )}
          dataTestId="dat-delete-modal"
          buttons={[
            {
              id: '2',
              label: t(
                'productionsAndExports.createNewProduction.datComponent.cancel'
              ),
              onClick: () => setShowDeleteModal(false),
              variant: 'secondary',
              dataTestId: 'bot2-Msg1',
            },
            {
              id: '1',
              label: t(
                'productionsAndExports.createNewProduction.datComponent.delete'
              ),
              onClick: () => {
                if (rowToDelete !== null) removeField(rowToDelete);
                setShowDeleteModal(false);
                setRowToDelete(null);
              },
              variant: 'danger',

              dataTestId: 'bot1-Msg1',
            },
          ]}
        />
      )}
      {/* Tags Tree Panel */}
      <TagsTreePanel
        key={`tag-panel-${currentFieldId}-${selectedTags.length}`}
        tags={tagTreeData}
        type="flyout"
        isOpen={isTagPanelOpen}
        title={t(
          'productionsAndExports.createNewProduction.datComponent.selectTags'
        )}
        onClose={() => {
          setIsTagPanelOpen(false);
          setCurrentFieldId(null);
        }}
        selectedTags={selectedTags}
        setSelectedTags={handleTagSelection}
      />
    </>
  );
};

export default DatComponent;
